import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { prepareFileForUpload } from '../utils/imageUploadCompress';

const FOLDERS = [
  'inbox', 'sent', 'drafts', 'starred', 'important', 'outbox', 'spam', 'trash',
];

const EMPTY_COMPOSE = {
  to: [],
  cc: [],
  bcc: [],
  subject: '',
  body_html: '',
  attachment_tokens: [],
  showCc: false,
  showBcc: false,
};

function isSyncDue(mailStatus) {
  if (!mailStatus?.configured) return false;
  if (!mailStatus.last_sync_at) return true;
  if (mailStatus.last_sync_status === 'error') return true;
  const intervalMin = mailStatus.account?.sync_interval_minutes ?? 5;
  const lastSync = new Date(mailStatus.last_sync_at).getTime();
  if (Number.isNaN(lastSync)) return true;
  return Date.now() - lastSync >= intervalMin * 60 * 1000;
}

export default function useCrmMail() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState({ configured: false, unread_counts: {}, total_unread: 0 });
  const [folder, setFolder] = useState(searchParams.get('folder') || 'inbox');
  const [messages, setMessages] = useState([]);
  const [meta, setMeta] = useState({ page: 1, total_pages: 1, total: 0 });
  const [selectedId, setSelectedId] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [thread, setThread] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState(EMPTY_COMPOSE);
  const [composeMode, setComposeMode] = useState('compose');
  const [replyToId, setReplyToId] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [error, setError] = useState(null);
  const draftTimerRef = useRef(null);
  const listIndexRef = useRef(-1);
  const syncingRef = useRef(false);
  const initialSyncDoneRef = useRef(false);
  const metaRef = useRef(meta);
  metaRef.current = meta;

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/crm/mail/status');
      const data = res.data?.data || { configured: false };
      setStatus(data);
      setError(null);
      if (!data.configured) {
        setLoadingList(false);
      }
    } catch (err) {
      setStatus({ configured: false });
      setError(err.response?.data?.message || 'Failed to load mail status');
      setLoadingList(false);
    }
  }, []);

  const loadMessages = useCallback(async (page = 1) => {
    if (!status.configured) return;
    setLoadingList(true);
    try {
      const res = await api.get('/crm/mail/messages', {
        params: {
          folder,
          page,
          limit: 25,
          q: searchQuery || undefined,
          filter: filter || undefined,
        },
      });
      const data = res.data?.data || {};
      setMessages(data.items || []);
      setMeta(data.meta || { page: 1, total_pages: 1, total: 0 });
    } catch (err) {
      setMessages([]);
      setError(err.response?.data?.message || 'Failed to load messages');
    } finally {
      setLoadingList(false);
    }
  }, [status.configured, folder, searchQuery, filter]);

  const runSync = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    try {
      await api.post('/crm/mail/sync');
      await loadMessages(metaRef.current.page);
      await loadStatus();
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to sync mail');
      await loadStatus();
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, [loadMessages, loadStatus]);

  const loadMessage = useCallback(async (id) => {
    if (!id || String(id).startsWith('draft-')) return;
    setLoadingMessage(true);
    try {
      const res = await api.get(`/crm/mail/messages/${id}`);
      const data = res.data?.data || {};
      setSelectedMessage(data.message || null);
      setThread(data.thread || []);
      setSelectedId(id);
      setMessages((prev) => prev.map((m) => (
        m.id === id ? { ...m, is_read: true } : m
      )));
    } catch {
      setSelectedMessage(null);
      setThread([]);
    } finally {
      setLoadingMessage(false);
    }
  }, []);

  const searchRecipients = useCallback(async (q) => {
    try {
      const res = await api.get('/crm/mail/recipients', { params: { q } });
      setRecipients(res.data?.data?.items || []);
    } catch {
      setRecipients([]);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  useEffect(() => {
    if (!status.configured) {
      initialSyncDoneRef.current = false;
      return;
    }
    if (!initialSyncDoneRef.current && isSyncDue(status)) {
      initialSyncDoneRef.current = true;
      runSync();
    }
  }, [status.configured, status.last_sync_at, status.last_sync_status, status.account?.sync_interval_minutes, runSync]);

  useEffect(() => {
    if (!status.configured) return undefined;
    const intervalMin = status.account?.sync_interval_minutes ?? 5;
    const intervalMs = Math.max(intervalMin * 60 * 1000, 60000);
    const id = setInterval(async () => {
      try {
        const res = await api.get('/crm/mail/status');
        const latest = res.data?.data || {};
        if (isSyncDue(latest)) {
          await runSync();
        }
      } catch {
        // ignore periodic status check errors
      }
    }, intervalMs);
    return () => clearInterval(id);
  }, [status.configured, status.account?.sync_interval_minutes, runSync]);

  useEffect(() => {
    if (status.configured) loadMessages(1);
  }, [status.configured, folder, searchQuery, filter, loadMessages]);

  useEffect(() => {
    const msgParam = searchParams.get('message');
    if (msgParam && status.configured) {
      loadMessage(Number(msgParam));
      const next = new URLSearchParams(searchParams);
      next.delete('message');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, status.configured, loadMessage, setSearchParams]);

  useEffect(() => {
    if (!composeOpen || !status.configured) return undefined;
    draftTimerRef.current = setInterval(async () => {
      if (!compose.subject && !compose.body_html && compose.to.length === 0) return;
      try {
        const res = await api.post('/crm/mail/drafts', {
          draft_id: draftId,
          draft: compose,
        });
        setDraftId(res.data?.data?.id || draftId);
      } catch { /* ignore draft save errors */ }
    }, 30000);
    return () => clearInterval(draftTimerRef.current);
  }, [composeOpen, compose, draftId, status.configured]);

  const selectFolder = (f) => {
    setFolder(f);
    setFilter('');
    setSearchQuery('');
    setSelectedId(null);
    setSelectedMessage(null);
    setThread([]);
    setSearchParams(f === 'inbox' ? {} : { folder: f }, { replace: true });
  };

  const openCompose = (mode = 'compose', message = null) => {
    setComposeMode(mode);
    setReplyToId(message?.id || null);
    if (mode === 'reply' && message) {
      const from = message.from || {};
      setCompose({
        ...EMPTY_COMPOSE,
        to: [{ email: from.email, name: from.name }],
        subject: message.subject?.match(/^re:/i) ? message.subject : `Re: ${message.subject || ''}`,
        body_html: `<br/><br/><blockquote>${message.body_html || message.snippet || ''}</blockquote>`,
      });
    } else if (mode === 'reply_all' && message) {
      const from = message.from || {};
      const toList = [from, ...(message.to || [])].filter((r) => r?.email);
      setCompose({
        ...EMPTY_COMPOSE,
        to: toList,
        cc: message.cc || [],
        subject: message.subject?.match(/^re:/i) ? message.subject : `Re: ${message.subject || ''}`,
        body_html: `<br/><br/><blockquote>${message.body_html || message.snippet || ''}</blockquote>`,
        showCc: (message.cc || []).length > 0,
      });
    } else if (mode === 'forward' && message) {
      setCompose({
        ...EMPTY_COMPOSE,
        subject: message.subject?.match(/^fwd:/i) ? message.subject : `Fwd: ${message.subject || ''}`,
        body_html: `<br/><br/>---------- Forwarded message ----------<br/>${message.body_html || message.snippet || ''}`,
      });
    } else {
      setCompose(EMPTY_COMPOSE);
      setDraftId(null);
    }
    setComposeOpen(true);
  };

  const closeCompose = () => {
    setComposeOpen(false);
    setCompose(EMPTY_COMPOSE);
    setReplyToId(null);
    setDraftId(null);
  };

  const sendMail = async (composeOverride) => {
    const payload = composeOverride ?? compose;
    setSending(true);
    try {
      const res = await api.post('/crm/mail/messages/send', {
        to: payload.to,
        cc: payload.cc,
        bcc: payload.bcc,
        subject: payload.subject,
        body_html: payload.body_html,
        attachment_tokens: payload.attachment_tokens,
        mode: composeMode,
        reply_to_message_id: replyToId,
      });
      if (res.data?.success) {
        closeCompose();
        loadMessages(1);
        loadStatus();
        if (folder !== 'sent') selectFolder('sent');
      }
      return res.data;
    } catch (err) {
      throw err;
    } finally {
      setSending(false);
    }
  };

  const performAction = async (messageId, action) => {
    await api.post(`/crm/mail/messages/${messageId}/actions`, { action });
    loadMessages(meta.page);
    loadStatus();
    if (selectedId === messageId) {
      if (action === 'delete') {
        setSelectedId(null);
        setSelectedMessage(null);
        setThread([]);
      } else {
        loadMessage(messageId);
      }
    }
  };

  const uploadAttachment = async (file) => {
    const prepared = await prepareFileForUpload(file);
    const formData = new FormData();
    formData.append('file', prepared);
    setUploadProgress(0);
    const res = await api.post('/crm/mail/attachments', formData, {
      onUploadProgress: (e) => {
        if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    setUploadProgress(null);
    const token = res.data?.data?.attachment_token;
    if (token) {
      setCompose((prev) => ({
        ...prev,
        attachment_tokens: [...(prev.attachment_tokens || []), token],
      }));
    }
    return res.data?.data;
  };

  const manualSync = () => runSync();

  const retryOutbox = async (outboxId) => {
    await api.post(`/crm/mail/outbox/${outboxId}/retry`);
    loadMessages(meta.page);
    loadStatus();
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      if (composeOpen) return;
      switch (e.key) {
        case 'c':
          openCompose();
          break;
        case '/':
          e.preventDefault();
          document.getElementById('crm-mail-search')?.focus();
          break;
        case 'r':
          if (selectedMessage) openCompose('reply', selectedMessage);
          break;
        case 'e':
          if (selectedId) performAction(selectedId, 'delete');
          break;
        case 'j':
          listIndexRef.current = Math.min(listIndexRef.current + 1, messages.length - 1);
          if (messages[listIndexRef.current]) loadMessage(messages[listIndexRef.current].id);
          break;
        case 'k':
          listIndexRef.current = Math.max(listIndexRef.current - 1, 0);
          if (messages[listIndexRef.current]) loadMessage(messages[listIndexRef.current].id);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const clearSelection = () => {
    setSelectedId(null);
    setSelectedMessage(null);
    setThread([]);
  };

  return {
    FOLDERS,
    status,
    folder,
    messages,
    meta,
    selectedId,
    selectedMessage,
    thread,
    loadingList,
    loadingMessage,
    searchQuery,
    setSearchQuery,
    filter,
    setFilter,
    composeOpen,
    compose,
    setCompose,
    composeMode,
    sending,
    syncing,
    uploadProgress,
    recipients,
    error,
    selectFolder,
    loadStatus,
    loadMessages,
    loadMessage,
    openCompose,
    closeCompose,
    sendMail,
    performAction,
    uploadAttachment,
    manualSync,
    retryOutbox,
    searchRecipients,
    clearSelection,
  };
}
